import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/hooks.ts'
import { selectUser } from '@/features/user/user.slice.ts'
import { UserInfo } from '@/apis/auth/auth.def.ts'
import { formatCurrency } from '@/utils/formatters.ts'
import { getBookIdeaLink } from '@/pages/components/book-idea-link.ts'
import { RankRow, RankCardData, MASKED_NAME } from './ranking-card.types.ts'

interface UseRankCardsParams {
  rankingList: RankRow[]
  groupByFirm: boolean
  startDate: string | null
  endDate: string | null
}

// Shared derivation of the ranking view models. Both the mobile card view and
// the desktop top-3 highlight resolve identity, self detection, masking and the
// idea-list link the same way, so the logic lives here once.
export function useRankCards({
  rankingList,
  groupByFirm,
  startDate,
  endDate,
}: UseRankCardsParams) {
  const { t } = useTranslation()
  const user: UserInfo = useAppSelector(selectUser)

  const isSelfRow = useMemo(
    () =>
      (row: RankRow): boolean =>
        groupByFirm
          ? !!user?.firmName && row.firmName === user.firmName
          : !!user?.bookId && row.bookId === user.bookId,
    [groupByFirm, user?.firmName, user?.bookId]
  )

  const selfRow = useMemo(
    () => rankingList.find((row) => isSelfRow(row)),
    [rankingList, isSelfRow]
  )

  // The podium/highlight (medals) is only meaningful when there is a single
  // first place. Ties at rank 1 (or a missing #1) collapse into the flat list.
  const hasSingleFirst = useMemo(
    () => rankingList.filter((row) => row.rank === 1).length === 1,
    [rankingList]
  )

  const toCardData = useMemo(
    () =>
      (row: RankRow): RankCardData => {
        const isSelf = isSelfRow(row)
        const isMasked = groupByFirm
          ? row.firmName === MASKED_NAME
          : row.userName === MASKED_NAME
        const baseIdentity = groupByFirm ? row.firmName : row.bookName
        const identity = isSelf
          ? groupByFirm
            ? t('overall_ranking.mobile_card.your_firm')
            : t('overall_ranking.mobile_card.you')
          : baseIdentity
        const { href, isInactive } = getBookIdeaLink({
          userId: row.userId,
          userName: row.userName,
          bookStatus: row.bookStatus,
          canSeeBookDetail: row.canSeeBookDetail,
          groupByFirm,
          startDate,
          endDate,
        })
        return {
          key: `${row.rank}-${row.bookId}`,
          rank: row.rank,
          rankTotal: row.rankTotal,
          identity,
          isSelf,
          isMasked,
          // Star score only applies to individual, unmasked books.
          score: groupByFirm || isMasked ? undefined : row.score,
          returnPct: row.returnPct,
          cumulativePnl: row.adjustedPnl,
          dailyPnl: row.adjustedDailyPnl,
          href,
          isInactive,
        }
      },
    [isSelfRow, groupByFirm, t, startDate, endDate]
  )

  return { isSelfRow, selfRow, hasSingleFirst, toCardData }
}

export function usdText(value: number): string {
  return formatCurrency(value, { decimal: 0, currency: 'USD' })
}
